import { Base2Presenter } from "core/presenter";
import {
  CompanyRepository,
  UserRepository,
  PlanRepository
} from "core/repository";

import { fn } from "util";
import _ from "lodash";

class SupportCompanyShowPresenter extends Base2Presenter {
  initialState() {
    return {
      company: {},
      plan: "",
      users: [],
      loading: true,
      order: {
        field: "name",
        direction: "ASC"
      },
      pagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      },
      filters: [],
      sellablesTest: {
        status: "",
        progress: "",
        errors: [],
        nfce_emission: "",
        nfces: []
      },
      infoTest: {
        status: "",
        progress: "",
        errors: []
      },
      companiesOptions: {
        online: true,
        repository: CompanyRepository,
        order: [{ field: "slug", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: async filter => await this.getFilter(filter)
      }
    };
  }

  async onStart() {
    await this.load();
  }

  async load() {
    const companyUuid = this.routeParams.companyUuid;

    const result = await CompanyRepository.get(companyUuid);
    const company = result.data;

    const plansResult = await PlanRepository.getAll({});
    const plans = plansResult.data;
    const plan = _.find(plans, plan => {
      return plan.uuid === company.plan_uuid;
    });

    this.enqueue({ plan, company, loading: false });
    this.getUsersByCompany(companyUuid);
  }

  timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async getSellablesNfce(companyUuid) {
    let ctrl = "pending";

    this.enqueue(vm => ({
      sellablesTest: {
        ...vm.sellablesTest,
        nfce_emission: "pending"
      }
    }));

    while (ctrl !== "done") {
      const result = await CompanyRepository.validateSellablesNfce(companyUuid);
      const { status, validations } = result.data;
      ctrl = status;

      this.enqueue(vm => ({
        sellablesTest: {
          ...vm.sellablesTest,
          nfce_emission: status,
          nfces: validations.map(nfce => ({
            nfce_id: nfce.nfce_id,
            status: nfce.nfce_response.nfce.status,
            xml_url: nfce.nfce_response.nfce.xml_url,
            response: nfce.nfce_response.nfce.data.resposta_emissao,
            sellable: nfce.sellable
          }))
        }
      }));

      await this.timeout(3000);
    }
  }

  async sellableEdit(uuid, company) {
    const currentUser = await this.mRuntime.get("currentUser");
    if (currentUser.company_uuid === company.uuid) {
      this.navigateTo("app.registration.product.edit", { productUuid: uuid });
    } else {
      this.sendErrorNotification(
        `Para alterar esse produto, é necessário que troque o seu usuário para empresa: ${company.name}`
      );
    }
  }

  async getInfoStatus(companyUuid) {
    const result = await CompanyRepository.validateInfoStatus(companyUuid);
    const { status, fields, progress } = result.data;
    const fieldsErrors = [];

    if (status === "failed") {
      _.map(fields, field => {
        fieldsErrors.push(`${field.field_name}: ${field.message}`);
      });
    }

    this.enqueue({
      infoTest: {
        status: status || "",
        progress: progress,
        errors: fieldsErrors
      }
    });
  }

  async validateSellables(companyUuid) {
    this.enqueue({
      sellablesTest: {
        status: "in_progress"
      }
    });

    const response = await CompanyRepository.validateSellables(companyUuid);
    const { valid, invalidSellables } = response.data;

    this.enqueue({
      sellablesTest: {
        status: valid ? "success" : "failed",
        errors: invalidSellables
      }
    });

    this.getSellablesNfce(companyUuid);
  }

  async validateInfo(companyUuid) {
    this.enqueue({
      infoTest: {
        status: "in_progress"
      }
    });

    const response = await CompanyRepository.validateInfo(companyUuid);
    const { valid, errors } = response.data;

    const fieldsErrors = errors.map(field => {
      const field_name = _.isUndefined(field.key) ? field.path[0] : field.key;
      return `${field_name}: ${field.message}`;
    });

    this.enqueue({
      infoTest: {
        status: valid ? "success" : "failed",
        errors: fieldsErrors
      }
    });
  }

  async getUsersByCompany(companyUuid) {
    this.enqueue({ loading: true });

    const order = [this.viewModel.order];
    const pagination = this.viewModel.pagination;
    const filters = this.viewModel.filters;

    const repositoryResult = await UserRepository.getListOnlineByCompany(
      companyUuid,
      {
        order,
        pagination: {
          page: pagination.currentPage,
          perPage: pagination.perPage
        },
        filters
      }
    );

    if (repositoryResult.success) {
      this.enqueue(vm => ({
        users: repositoryResult.data,
        pagination: {
          ...vm.pagination,
          total: repositoryResult.total,
          perPage: repositoryResult.perPage
        }
      }));
    } else {
      this.sendNotification({
        type: "error",
        title: "Ooops...",
        content:
          "Ocorreu um erro. Não foi possível carregar os usuários dessa empresa. Tente novamente mais tarde."
      });
    }

    this.enqueue({ loading: false });
  }

  onChangeSwitch({ name, uuid, company_uuid, deleted }, checked) {
    const message = deleted ? "ativar" : "desativar";

    this.sendNotification({
      type: "confirmation",
      title: "Você tem certeza?",
      content: `Tem certeza que deseja ${message} o(a) usuário(a) ${name}?`,
      actions: [
        {
          label: "Voltar"
        },
        {
          label: "Sim, eu tenho certeza",
          type: "accent",
          callback: () => this.callRepository(uuid, company_uuid, checked)
        }
      ]
    });
  }

  onFilterChange(fields) {
    const filters = [];

    if (!fn.isBlank(fields.email)) {
      filters.push({
        connector: "AND",
        field: "email",
        operation: "LIKE",
        value: fields.email
      });
    }

    if (!fn.isBlank(fields.name)) {
      filters.push({
        connector: "AND",
        field: "name",
        operation: "LIKE",
        value: fields.name
      });
    }

    this.enqueue({ filters }, () => this.load());
  }

  onPageChange(page) {
    this.enqueue(
      vm => ({
        pagination: {
          ...vm.pagination,
          currentPage: page
        }
      }),
      () => this.load()
    );
  }

  onOrderChange(field, direction) {
    this.enqueue(
      vm => ({
        order: {
          ...vm.order,
          field,
          direction
        }
      }),
      () => this.load()
    );
  }

  async callRepository(uuid, companyUuid, checked) {
    this.enqueue({ loading: true });

    const result = await UserRepository.deleteUserByCompany(
      uuid,
      companyUuid,
      !checked
    );

    const enable = checked ? "Habilitar" : "Desabilitar";
    const enableSuccess = checked ? "Habilitado(a)" : "Desabilitado(a)";

    if (result.success) {
      this.sendNotification({
        type: "success",
        content: `Usuário(a) ${enableSuccess} com sucesso!`
      });
    } else {
      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: `Ocorreu um erro e não foi possível ${enable} o(a) usuário(a). Tente novamente mais tarde.`
      });

      setTimeout(() => this.onChangeSwitch(uuid, !checked), 300);
    }

    await this.load();
  }

  onGoBack() {
    this.navigateTo("app.support.company");
  }

  openCloneSelector() {
    this.enqueue({ cloneSelector: true });
  }

  closeCloneSelector() {
    this.enqueue({ cloneSelector: false });
  }

  sendErrorNotification(message) {
    this.sendNotification({
      type: "error",
      title: "Oops...",
      content: message
    });
  }

  async onSelectCompany(company) {
    if (company.uuid === this.viewModel.company.uuid) {
      this.sendErrorNotification(
        `Selecione empresas diferentes para conseguir clonar os produtos`
      );
    } else {
      try {
        const response = await CompanyRepository.cloneCompanySellables({
          destination: company.uuid,
          origin: this.viewModel.company.uuid
        });
        if (response.success) {
          this.sendNotification({
            type: "success",
            title: "Produtos clonados com sucesso!",
            content: `Sucesso ao clonar os produtos da empresa ${this.viewModel.company.name} para empresa ${company.name}`
          });
        } else {
          this.sendErrorNotification(
            `Ocorreu um erro ao clonar os produtos da empresa ${this.viewModel.company.name} para empresa ${company.name}`
          );
        }
      } catch (error) {
        this.sendErrorNotification(
          `Ocorreu um erro ao clonar os produtos da empresa ${this.viewModel.company.name} para empresa ${company.name}`
        );
      }
    }
  }

  getFilter(filter) {
    return [
      { connector: "AND", field: "slug", operation: "LIKE", value: filter },
      {
        connector: "OR",
        field: "fiscal_informations.cnpj",
        operation: "LIKE",
        value: filter
      },
      {
        connector: "OR",
        field: "fiscal_informations.legal_name",
        operation: "LIKE",
        value: filter
      }
    ];
  }
}

export default SupportCompanyShowPresenter;



// WEBPACK FOOTER //
// ./src/components/app/support/company/company-show/presenter.js